import { useState, Fragment } from 'react';

import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';

import PropTypes from 'prop-types';

import Attributes, { buildSkuAttributeMap, buildLookAttributeMap } from './Attributes';
import { findSwatchByProductLookId } from './helpers';
import {
  renderPersonalizationAction,
  renderActionButton,
  renderQuantity,
  updateProductLook,
  MSRPToolTip,
  handleAttributeChange,
} from './ProductOverviewCore';
import { useAddToRegistryFromPDPKit } from './useAddToRegistryFromPDP';
import TileSwatchList from '../../components/common/shop/ProductTile/TileSwatchList';
import { formatPrice } from '../../util/currencyFormatter';
import { getProductPrices } from '../../util/productPrice';

const ProductOverviewRow = (props) => {
  const {
    productData,
    activeProductLook,
    setActiveProductLook,
    productAttributes,
    setProductAttributes,
    quantity,
    setQuantity,
    setActiveSku,
    activeSku,
    onPdpRedirect,
    handleAddToCart,
    userContext,
    location,
    section,
    collectionEventData,
  } = props;

  const handleAddToRegistry = useAddToRegistryFromPDPKit({
    activeProductLook,
    activeSku,
    productData,
    quantity,
    userContext,
    location,
    section,
    extraData: collectionEventData,
  });

  const [skuAttributeMap] = useState(
    productData ? buildSkuAttributeMap(productData.product_look_views) : {}
  );
  const [lookAttributeMap] = useState(
    productData ? buildLookAttributeMap(productData.attributes) : {}
  );

  if (!productData || !activeProductLook) return null;

  const productName = productData.display_name;
  const activeSwatch = findSwatchByProductLookId(activeProductLook.id, productData.swatches);
  const productPrices = getProductPrices(activeProductLook, activeSku);
  const { actions } = !activeSku ? activeProductLook : activeSku;
  const actionHandlers = {
    ADD_TO_REGISTRY: handleAddToRegistry,
    ADD_TO_CART: handleAddToCart,
  };

  const setters = {
    setActiveProductLook,
    setProductAttributes,
    setActiveSku,
    isKitProduct: true,
  };

  const handlePdpRedirect = (e) => {
    if (e) e.preventDefault();
    const openPersonalizationModal = true;
    onPdpRedirect(openPersonalizationModal);
  };

  const onSwatchChange = (productLookId) => {
    updateProductLook(productLookId, productData, setters);
  };

  const onAttributeChange = handleAttributeChange(
    productData,
    productAttributes,
    activeProductLook,
    setters
  );
  const mustBePersonalized = activeSku && activeSku.must_be_personalized;
  const isPersonalizable = activeSku && activeSku.personalizable && !activeSku.must_be_personalized;
  const renderPersonalizationLink = () => {
    const personalizationSurcharge = formatPrice(activeSku.personalization_surcharge_cents);
    return (
      <div className="product-detail-row__personalization-cta">
        <LinkV2 arrow href="" onClick={handlePdpRedirect}>
          Personalize It (+{personalizationSurcharge}/Ea.)
        </LinkV2>
      </div>
    );
  };

  return (
    <div className="product-overview-row_container">
      <div className="product-details_container">
        <div className="product-details">
          <div className="brand-product-names-nonmobile">
            <LinkV2
              noUnderline
              onClick={onPdpRedirect}
              href={`/shop/product/${activeProductLook.key}`}
              className="product-name"
            >
              {productName}
            </LinkV2>
          </div>
          {productPrices.msrp ? (
            <div className="msrp-price">
              MSRP
              <span data-tooltip={MSRPToolTip}>
                <i className="zolaicon-info" />
              </span>{' '}
              {productPrices.msrp}
            </div>
          ) : null}
          {productPrices.sale ? (
            <div className="sale-price">
              Was&nbsp;<span>{productPrices.sale}</span>
            </div>
          ) : null}
          {productPrices.discount ? (
            <div className="sale-price">
              Was&nbsp;<s>{productPrices.discount}</s>
            </div>
          ) : null}
          <div className="zola-price">{productPrices.zolaPrice}</div>
          <span className="price-matching-text">
            <LinkV2
              variant="primary"
              noUnderline
              href="/faq/115002838511-does-zola-offer-price-matching-what-is-zola-s-price-matching-policy-"
              className="base-text"
            >
              Price Matching Available
            </LinkV2>
          </span>
          {activeSwatch ? (
            <div className="active-color">
              <span>{productData.swatch_group_label}:</span> {activeSwatch.label}
            </div>
          ) : null}
          <TileSwatchList
            activeProductLookId={activeProductLook.id}
            limit={99}
            swatches={productData.swatches}
            onSwatchChange={onSwatchChange}
          />
          <Attributes
            productData={productData}
            activeProductLook={activeProductLook}
            productAttributes={productAttributes}
            setProductAttributes={setProductAttributes}
            skuAttributeMap={skuAttributeMap}
            lookAttributeMap={lookAttributeMap}
            onAttributeChange={onAttributeChange}
          />
          {isPersonalizable && renderPersonalizationLink()}
        </div>
        <div className="user-input-form">
          {renderQuantity(productData, quantity, setQuantity)}
          <div className="action-buttons">
            {mustBePersonalized
              ? renderPersonalizationAction(handlePdpRedirect)
              : actions.map((action, i) => (
                  <Fragment key={i}>
                    {renderActionButton(action, actionHandlers[action.key])}
                  </Fragment>
                ))}
          </div>
          {activeSku && activeSku.stock_message && (
            <div className="stock-message_container">
              <div className="stock-message-text">{activeSku.stock_message}</div>
            </div>
          )}
          {activeSku && activeSku.free_shipping ? (
            <LinkV2 variant="primary" noUnderline href="/shipping" className="free-shipping-text">
              <span className="fast-shipping-icon" />
              Free Shipping & Returns
            </LinkV2>
          ) : null}
          {!!activeSku?.delivery_surcharge_cents && (
            <LinkV2 variant="primary" noUnderline href="/shipping" className="free-shipping-text">
              Delivery Surcharge: {formatPrice(activeSku.delivery_surcharge_cents)}
            </LinkV2>
          )}
        </div>
      </div>
    </div>
  );
};

ProductOverviewRow.propTypes = {
  productData: PropTypes.shape({}), // fetchProductByKey payload
  activeProductLook: PropTypes.shape({}), // fetchProductByKey payload > product_look_views
  setActiveProductLook: PropTypes.func.isRequired,
  productAttributes: PropTypes.shape({}),
  setProductAttributes: PropTypes.func.isRequired,
  quantity: PropTypes.number,
  setActiveSku: PropTypes.func.isRequired,
  activeSku: PropTypes.shape({}), // fetchProductByKey payload > sku_previews
  handleAddToCart: PropTypes.func.isRequired,
  setQuantity: PropTypes.func.isRequired,
  onPdpRedirect: PropTypes.func,
};

export default ProductOverviewRow;
